import { h, Component } from "preact";
import { Field } from "redux-form";
import ReactGoogleMapLoader from "react-google-maps-loader";
import { css } from "emotion";
import Required from "./Required";
import Description from "./Description";
import config from "../../config";
import {
  getCountry,
  getState,
  getSuburb,
  getPostalCode
} from "../../utils/Address";
import InputField from "./InputField";
import { required } from "../../utils/Validators";

const styles = {
  locationWrapper: css({
    display: "flex",
    "@media(max-width: 600px)": {
      display: "block"
    }
  }),
  leftColumn: css({
    flex: 1,
    "@media(max-width: 600px)": {
      marginRight: 0,
      display: "block"
    }
  }),
  list: css({
    position: "relative",
    display: "block",
    fontSize: 16,
    marginTop: 0,
    marginBottom: 0,
    cursor: "pointer"
  }),
  locationManualContainer: css({
    display: "flex",
    flexWrap: "wrap"
  }),
  streetAddress: css({
    width: "100%"
  }),
  locationManualFields: css({
    width: "49%"
  }),
  locationManualLeftFields: css({
    width: "49%",
    marginRight: 8
  })
};

const AddressField = ({
  input,
  label,
  description,
  inputType,
  required,
  placeholder,
  disabled,
  style,
  inputStyle,
  onPlaceChanged,
  meta: { touched, error }
}) => {
  let invalidObj = {};
  if (required) {
    invalidObj = {
      "aria-invalid": error && touched ? "true" : "false"
    };
  }
  return (
    <div
      class={touched && error ? "form-group has-error" : "form-group"}
      style={style}
    >
      {label ? (
        <label for={input.name} class="form-label">
          {label}
          <Required required={required} />
        </label>
      ) : (
        ""
      )}
      <Description description={description} />
      <input
        class="form-input"
        id={input.name}
        disabled={disabled}
        type={inputType}
        placeholder={placeholder}
        {...input}
        style={inputStyle}
        aria-label={label ? `${label}` : `${inputType}`}
        {...invalidObj}
        aria-required={required ? "true" : "false"}
        onkeyup={e => {
          onPlaceChanged(e);
          input.onChange(e);
        }}
        aria-describedby={`${input.name}Error`}
      />
      {touched && error && (
        <p class="form-input-hint" id={`${input.name}Error`}>
          {error}
        </p>
      )}
    </div>
  );
};
class Location extends Component {
  constructor(props) {
    super(props);
    this.googleMaps = null;
    this.autocomplete = null;
    this.googleService = null;
    this.sessionToken = null;
    this.createAutoComplete = this.createAutoComplete.bind(this);
    this.state = {
      showAddressList: false,
      showAddressManualFields: false,
      predictions: [],
      noAddress: false
    };
  }

  nameRequired = required("Ticket Type is required");

  componentDidMount() {
    this.createAutoComplete();
  }

  createAutoComplete() {
    const { googleMaps } = this.props;
    if (googleMaps) {
      this.googleMaps = googleMaps;
      // token is required to prevent double charges from Google for two API calls of AutocompleteService and PlacesService
      // we only get charged when we call PlacesService with token
      this.sessionToken = new googleMaps.places.AutocompleteSessionToken();
      this.autocomplete = new googleMaps.places.AutocompleteService();
      this.googleService = new googleMaps.places.PlacesService(
        document.createElement("span")
      );
    } else {
      setTimeout(this.createAutoComplete, 300);
    }
  }

  onPlaceChanged = e => {
    const value = e.target.value ? e.target.value : false;

    if (value) {
      const displaySuggestions = (predictions, status) => {
        if (status != this.googleMaps.places.PlacesServiceStatus.OK) {
          return;
        }
        this.setState({ predictions: predictions });
      };
      this.autocomplete.getQueryPredictions(
        { input: value, sessionToken: this.sessionToken },
        displaySuggestions
      );
      this.setState({ showAddressList: true });
    }
  };

  clickAddress = async value => {
    const { changeFieldValue, questionName } = this.props;
    if (value === "noAddress") {
      changeFieldValue(`${questionName}.location`, "");
      changeFieldValue(`${questionName}.details.country`, "");
      changeFieldValue(`${questionName}.details.state`, "");
      changeFieldValue(`${questionName}.details.postalCode`, "");
      changeFieldValue(`${questionName}.details.suburb`, "");
      changeFieldValue(`${questionName}.details.street`, "");
      this.setState({ noAddress: true });
    } else {
      const fullAddress = await this.getAddressDetails(value);
      const country = getCountry(fullAddress.address_components);
      const state = getState(fullAddress.address_components);
      const suburb = getSuburb(fullAddress.address_components);
      const postalCode = getPostalCode(fullAddress.address_components);
      const streetAddress = value.structured_formatting.main_text;
      changeFieldValue(`${questionName}.location`, value.description);
      changeFieldValue(`${questionName}.details.country`, country);
      changeFieldValue(`${questionName}.details.state`, state);
      changeFieldValue(`${questionName}.details.postalCode`, postalCode);
      changeFieldValue(`${questionName}.details.suburb`, suburb);
      changeFieldValue(`${questionName}.details.street`, streetAddress);
    }
    this.setState({ showAddressList: false, showAddressManualFields: true });
  };

  getAddressDetails = async value => {
    var request = {
      placeId: value.place_id,
      sessionToken: this.sessionToken,
      fields: ["formatted_address", "address_component"]
    };
    return new Promise((resolve, reject) => {
      this.googleService.getDetails(request, (place, status) => {
        if (status == this.googleMaps.places.PlacesServiceStatus.OK) {
          resolve(place);
        }
      });
    });
  };

  render() {
    const {
      questionName,
      label,
      addQuestionQuantityCheck,
      meta: { touched, error, warning }
    } = this.props;
    const isRequired = this.props.required;
    const {
      showAddressList,
      showAddressManualFields,
      predictions,
      noAddress
    } = this.state;
    const customStyles = {
      options: css({
        zIndex: 10001,
        overflowX: "auto",
        backgroundColor: "#fff",
        position: "absolute",
        margin: 0,
        border: "0.05rem solid #e7e7e7",
        borderRadius: 4,
        width: "100%",
        maxWidth: 522,
        padding: 0,
        paddingLeft: 10,
        display: showAddressList ? "block" : "none",
        color: "#323232",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)"
      })
    };

    const suggestionList = (
      <ul className={customStyles.options}>
        {predictions.length
          ? predictions.map(prediction => (
              <li
                className={styles.list}
                onClick={() => this.clickAddress(prediction)}
              >
                {prediction.description}
              </li>
            ))
          : null}
        <li
          className={styles.list}
          onClick={() => this.clickAddress("noAddress")}
        >
          Can't find your address?
        </li>
      </ul>
    );
    return (
      <div className={styles.locationWrapper}>
        <div className={styles.leftColumn}>
          <div class={touched && error ? "form-group has-error" : "form-group"}>
            {!noAddress ? (
              <div>
                <Field
                  label={label}
                  component={AddressField}
                  showAddressList={showAddressList}
                  clickAddress={this.clickAddress}
                  onPlaceChanged={this.onPlaceChanged}
                  name={`${questionName}.location`}
                  required={isRequired}
                  validate={
                    isRequired
                      ? [required, addQuestionQuantityCheck]
                      : [addQuestionQuantityCheck]
                  }
                />
                {suggestionList}
              </div>
            ) : null}
            {showAddressManualFields ? (
              <div className={styles.locationManualContainer}>
                <div className={styles.streetAddress}>
                  <Field
                    label="Street Address"
                    component={InputField}
                    name={`${questionName}.details.street`}
                    autocomplete="nope"
                    component={InputField}
                    required={isRequired}
                    validate={
                      isRequired
                        ? [required, addQuestionQuantityCheck]
                        : [addQuestionQuantityCheck]
                    }
                  />
                  {touched && error && <p class="form-input-hint">{error}</p>}
                </div>
                <div className={styles.locationManualLeftFields}>
                  <Field
                    label="Suburb"
                    component={InputField}
                    name={`${questionName}.details.suburb`}
                    autocomplete="nope"
                    component={InputField}
                    required={isRequired}
                    validate={
                      isRequired
                        ? [required, addQuestionQuantityCheck]
                        : [addQuestionQuantityCheck]
                    }
                  />
                  {touched && error && <p class="form-input-hint">{error}</p>}
                </div>
                <div className={styles.locationManualFields}>
                  <Field
                    label="State"
                    component={InputField}
                    name={`${questionName}.details.state`}
                    autocomplete="nope"
                    component={InputField}
                    required={isRequired}
                    validate={
                      isRequired
                        ? [required, addQuestionQuantityCheck]
                        : [addQuestionQuantityCheck]
                    }
                  />
                  {touched && error && <p class="form-input-hint">{error}</p>}
                </div>
                <div className={styles.locationManualLeftFields}>
                  <Field
                    label="Postcode"
                    component={InputField}
                    name={`${questionName}.details.postalCode`}
                    autocomplete="nope"
                    component={InputField}
                    required={isRequired}
                    validate={
                      isRequired
                        ? [required, addQuestionQuantityCheck]
                        : [addQuestionQuantityCheck]
                    }
                  />
                </div>
                <div className={styles.locationManualFields}>
                  <Field
                    label="Country"
                    component={InputField}
                    name={`${questionName}.details.country`}
                    autocomplete="nope"
                    component={InputField}
                    required={isRequired}
                    validate={
                      isRequired
                        ? [required, addQuestionQuantityCheck]
                        : [addQuestionQuantityCheck]
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Autocomplete input filed for address
 *
 * @param {*} props
 * @returns
 */
const LocationField = props => {
  return (
    <ReactGoogleMapLoader
      params={{
        key: config("GOOGLE_API_KEY"), // Define your api key here
        libraries: "places"
      }}
      render={(googleMaps, error) =>
        googleMaps ? (
          <div>
            {/*Show a custom error if SDK Authentication Error. See N/B 2 below.*/}
            {error ? error : <Location {...props} googleMaps={googleMaps} />}
          </div>
        ) : (
          <div>
            {/*Check for network error so loading state ends if user lost connection.*/}
            {error === "Network Error" ? <p>{error}</p> : <p>isLoading...</p>}
          </div>
        )
      }
    />
  );
};

export default LocationField;



// WEBPACK FOOTER //
// ./components/fields/LocationField.js